import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import { api } from '../lib/api.ts';
import { useGridQuery, useGridState } from '../lib/grid.ts';
import { LevelBadge, ymd } from '../lib/notices.tsx';
import { dt } from '../lib/platform.ts';
import { DataTable, type Column } from '../components/DataTable.tsx';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { PageActions } from '../components/ui/PageActions.tsx';
import { Badge, LinkButton } from '../components/ui/Button.tsx';

/** 앱 내 공지 (CR-035): 게시 시작이 지난 전체공지 + 내 테넌트 대상 공지. 운영 정보 없음 */
type TenantNotice = { id: string; title: string; level: string; startsAt: string; endsAt: string; createdAt: string; state: 'active' | 'ended' };
type TenantNoticeDetail = TenantNotice & { body: string };

const STATE: Record<string, { label: string; tone: 'success' | 'neutral' }> = { active: { label: '게시 중', tone: 'success' }, ended: { label: '종료', tone: 'neutral' } };

export function TenantNoticesPage() {
  const { state, set } = useGridState('tn', { sort: { col: 'startsAt', dir: 'desc' }, pageSize: 20 });
  const data = useGridQuery<TenantNotice>('tenant-notices', '/tenant/notices/grid', state);
  const columns: Column<TenantNotice>[] = [
    { key: 'level', label: '수준', width: '5rem', filter: 'select', render: (n) => <LevelBadge level={n.level} /> },
    { key: 'title', label: '제목', filter: 'text', render: (n) => <Link className="font-semibold text-slate-800 hover:text-accent-strong hover:underline" to={`/notices/${n.id}`}>{n.title}</Link> },
    { key: 'state', label: '상태', width: '5.5rem', sortable: false, render: (n) => <Badge tone={STATE[n.state].tone}>{STATE[n.state].label}</Badge> },
    { key: 'startsAt', label: '게시 시작', width: '9rem', filter: 'date', render: (n) => <span className="whitespace-nowrap text-[12px]">{dt(n.startsAt)}</span> },
    { key: 'endsAt', label: '게시 종료', width: '9rem', filter: 'date', render: (n) => <span className="whitespace-nowrap text-[12px]">{dt(n.endsAt)}</span> },
    { key: 'createdAt', label: '등록일', width: '7rem', filter: 'date', render: (n) => ymd(n.createdAt) },
  ];
  return (
    <div>
      <PageHeader title="공지사항" description="PRISM 전체 공지와 우리 조직을 대상으로 한 공지입니다. 게시 중인 공지는 화면 상단 배너에도 표시됩니다." />
      {data.isError && <div className="text-red-600 text-sm mb-2">{(data.error as Error).message}</div>}
      <DataTable id="notices" columns={columns} data={data.data} state={state} onChange={set} isFetching={data.isFetching} emptyText="공지가 없습니다" />
    </div>
  );
}

export function TenantNoticePage() {
  const { id = '' } = useParams();
  const q = useQuery({ queryKey: ['tenant-notice', id], queryFn: () => api<TenantNoticeDetail>(`/tenant/notices/${id}`), retry: false });
  if (q.isLoading) return <div className="text-sm text-slate-500">불러오는 중…</div>;
  if (q.isError || !q.data) return <div><PageHeader back={{ to: '/notices', label: '공지사항' }} title="공지를 찾을 수 없습니다" description="없거나 우리 조직 대상이 아닌 공지입니다." /></div>;
  const n = q.data;
  return (
    <div>
      <PageHeader back={{ to: '/notices', label: '공지사항' }} title={n.title}>
        <LevelBadge level={n.level} />
        <Badge tone={STATE[n.state].tone}>{STATE[n.state].label}</Badge>
        <span className="text-[12px] text-slate-500">등록 {ymd(n.createdAt)} · 게시 {dt(n.startsAt)} ~ {dt(n.endsAt)}</span>
      </PageHeader>
      <div className="min-h-[8rem] border border-slate-200 bg-white p-3 text-[13px] leading-relaxed whitespace-pre-wrap">{n.body || <span className="text-slate-400">본문 없음</span>}</div>
      <PageActions><LinkButton variant="secondary" to="/notices">목록</LinkButton></PageActions>
    </div>
  );
}
